import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import dayjs from 'dayjs';
import moment from 'moment';
import { BooleanField, ChipField, Datagrid, DateField, DateInput, Filter, FunctionField, NumberField, ReferenceArrayField, ReferenceArrayInput, ReferenceField, ReferenceInput, SelectArrayInput, SelectField, SelectInput, SingleFieldList, TextField, useGetIdentity, useListContext } from 'react-admin';
import { CustomizableDatagrid, LazyLoadAutoCompleteInput, ListPagination, Permissions, ReportList } from '../../components';
import { useRowClick } from '../../hooks/useRowClick';
import { UserRole } from '../../providers/authProvider';
import { getChainId, getCompanyId } from '../../utils/utils';

const useStyles = makeStyles(theme => ({
  title: {
    margin: '10px 0 10px 0',
    textAlign: 'center',
    fontWeight: 'bold',
    paddingTop: 30
  },
  headerCell: {
    backgroundColor: '#E5E5E5',
  },
  rowWithoutInvoice: {
    backgroundColor: '#fff4e5',
  }
}));

const FillinList = () => {
  const classes = useStyles();
  const props = useListContext();
  const { identity } = useGetIdentity();
  const { handleRowClick } = useRowClick();

  let data = [];
  let summaryData = [];

  if (!props.data) { return <></>; }

  if (props.data) {
    const fillinInfo = props.data[0];
    if (fillinInfo) {
      data = fillinInfo.data;
      summaryData = [fillinInfo.summaryData];
    }
  }

  return (
    <>
      {identity?.role === UserRole.chain && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#fff4e5', border: '1px solid #ddd', borderRadius: 0.5 }} />
            <Typography variant="body2">Linhas destacadas indicam abastecimentos sem nota fiscal</Typography>
          </Box>
        </Box>
      )}
      <Typography className={classes.title}>Total</Typography>
      <Datagrid classes={{ headerCell: classes.headerCell }} bulkActionButtons={false} sx={{ marginBottom: '60px' }} total={1} data={summaryData}>
        <NumberField
          source="total_value_to_pay"
          label="Total a Pagar"
          textAlign="center"
          emptyText="R$ 0,00"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
          sortable={false}
        />
        <TextField
          source="total_fillins"
          textAlign="center"
          emptyText="--"
          label="Total de abastecimentos"
          sortable={false}
        />
      </Datagrid>
      <Typography className={classes.title}>Abastecimentos - Previsão de Pagamento</Typography>
      <CustomizableDatagrid
        classes={{ headerCell: classes.headerCell }}
        resource="fillins"
        data={data}
        rowClick={(id, resource, record) => handleRowClick('show', id, resource, record)}
        defaultColumns={['stationId', 'valueToPay', 'taxRate', 'dueDate', 'receivedAt', 'invoiceId', 'fuelId', 'isBilled', 'subCompanyBillingId']}
        bulkActionButtons={false}
        rowStyle={(record) => identity?.role === UserRole.chain && !record.invoiceId ? { backgroundColor: '#fff4e5' } : {}}
      >
        <ReferenceField
          label="Posto"
          emptyText="Posto não identificado"
          source="stationId"
          basePath="stations"
          reference="stations"
        >
          <TextField source="name" />
        </ReferenceField>

        <Permissions label="Rede" emptyText="Não identificada" userRoles={[UserRole.admin, UserRole.company]}>
          <ReferenceField source="chainId" basePath="chains" reference="chains" link={null}>
            <TextField label="Rede" emptyText="Não identificada" source="name" />
          </ReferenceField>
        </Permissions>

        <Permissions label="Transportadora" emptyText="Não identificada" userRoles={[UserRole.admin, UserRole.chain]}>
          <ReferenceField source="subCompanyId" basePath="sub-companies" reference="sub-companies" link={null}>
            <TextField label="Transportadora" emptyText="Não identificada" source="name" />
          </ReferenceField>
        </Permissions>

        <NumberField
          source="valueToPay"
          label="Valor a Pagar"
          locales="pt-BR"
          textAlign="center"
          emptyText="R$ 0,00"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
        />

        <NumberField
          source="taxRate"
          label="Taxa (%)"
          textAlign="center"
          emptyText="0%"
          options={{
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
        />

        <DateField
          source="dueDate"
          label="Vencimento Pagamento"
          showTime={false}
          locales="pt-BR"
        />

        <DateField
          source="receivedAt"
          label="Data do Abastecimento"
          showTime
          locales="pt-BR"
        />

        <ReferenceField
          label="Combustível"
          emptyText="Não identificado"
          source="fuelId"
          basePath="fuels"
          reference="fuels"
          link={identity?.role === UserRole.admin && "show"}
        >
          <TextField source="name" />
        </ReferenceField>

        <ReferenceField
          source="invoiceId"
          label="Nota Fiscal"
          emptyText="--"
          link={false}
          basePath="invoices"
          reference="invoices"
        >
          <TextField source="invoiceNumber" />
        </ReferenceField>

        <BooleanField
          source="isBilled"
          label="Faturado"
          textAlign="center"
          valueLabelTrue="Sim"
          valueLabelFalse="Não"
        />

        <ReferenceField
          source="subCompanyBillingId"
          label="Fatura"
          emptyText="--"
          link="show"
          basePath="sub-company-billings"
          reference="sub-company-billings"
        >
          <TextField source="numericId" />
        </ReferenceField>
      </CustomizableDatagrid>
    </>
  );
};

const FilterTablePrint = () => {
  const classes = useStyles();
  const { filterValues } = useListContext();
  return (
    <Datagrid classes={{ headerCell: classes.headerCell }} data={[{ ...filterValues, generatedReportAt: new Date() }]} total={1} bulkActionButtons={false}>
      <DateField source="from" textAlign="center" label="De" sortable={false} locales="pt-BR" />
      <DateField source="to" textAlign="center" label="Até" sortable={false} locales="pt-BR" />

      <Permissions userRoles={[UserRole.admin, UserRole.company]} label="Rede">
        <ReferenceField emptyText="Todas" source="chainId" textAlign="center" basePath="chains" reference="chains" link={false}>
          <TextField source="name" />
        </ReferenceField>
      </Permissions>

      <Permissions userRoles={[UserRole.admin, UserRole.chain]} label="Postos">
        <ReferenceArrayField emptyText="Todos" source="stationIds" textAlign="center" basePath="stations" reference="stations" link={false}>
          <SingleFieldList>
            <ChipField source="name" />
          </SingleFieldList>
        </ReferenceArrayField>
      </Permissions>

      <Permissions userRoles={[UserRole.admin, UserRole.chain]} label="Transportadora">
        <ReferenceField emptyText="Todas" source="subCompanyId" textAlign="center" basePath="sub-companies" reference="sub-companies" link={false}>
          <TextField source="name" />
        </ReferenceField>
      </Permissions>

      <ReferenceField label="Produto" emptyText="Todos" source="fuelId" textAlign="center" basePath="fuels" reference="fuels" link={false}>
        <TextField source="name" />
      </ReferenceField>

      <FunctionField source="hasInvoice" label="Nota Fiscal" render={record => record.hasInvoice === true ? 'Com Nota' : record.hasInvoice === false ? 'Sem Nota' : 'Todos'} />

      <FunctionField source="isBilled" label="Status de Faturamento" render={record => record.isBilled === true ? 'Faturado' : record.isBilled === false ? 'Não faturado' : 'Todos'} />

      <DateField source="generatedReportAt" textAlign="center" label="Gerado às" showTime sortable={false} locales="pt-BR" />
    </Datagrid>
  );
}

const BillingForecastPaymentReport = (props) => {
  const { identity } = useGetIdentity();

  const filterValue = identity?.role === UserRole.chain ? { chainId: getChainId() }
    : (identity?.role === UserRole.company ? { companyId: getCompanyId() } : {});

  const ListFilters = () => {
    const { filterValues } = useListContext();

    return (
      <Filter>
        <DateInput
          source="from"
          label="De"
          sx={{ marginBottom: 0.5 }}
          options={{ format: 'DD/MM/YYYY' }}
          alwaysOn
          parse={(value) => moment(value).startOf('day').toISOString()}
        />
        <DateInput
          source="to"
          label="Até"
          sx={{ marginBottom: 0.5 }}
          options={{ format: 'DD/MM/YYYY' }}
          alwaysOn
          parse={(value) => moment(value).endOf('day').toISOString()}
        />

        {
          Permissions({
            userRoles: [UserRole.admin, UserRole.company],
            children: (
              <ReferenceInput
                label="Rede"
                source="chainId"
                reference="chains"
                sort={{ field: "name", order: "ASC" }}
                emptyText="Todas"
                alwaysOn
              >
                <SelectInput
                  emptyText="Todas"
                  label="Rede"
                  optionText={'name'}
                />
              </ReferenceInput>
            )
          })
        }

        {
          Permissions({
            userRoles: [UserRole.admin, UserRole.chain],
            children: (
              <ReferenceArrayInput
                source="stationIds"
                reference="stations"
                sort={{ field: "name", order: "ASC" }}
                filter={identity?.role === UserRole.chain ? { chainId: getChainId() } : { chainId: filterValues.chainId || undefined }}
                alwaysOn
                perPage={null}
              >
                <SelectArrayInput
                  label="Posto"
                  optionText="name"
                />
              </ReferenceArrayInput>
            )
          })
        }

        {
          Permissions({
            userRoles: [UserRole.admin, UserRole.chain],
            children: (
              <ReferenceInput
                label="Transportadora"
                source="subCompanyId"
                reference="sub-companies"
                sort={{ field: "name", order: "ASC" }}
                emptyText="Todas"
                alwaysOn
              >
                <SelectInput
                  emptyText="Todas"
                  label="Transportadora"
                  optionText={'name'}
                />
              </ReferenceInput>
            )
          })
        }

        <ReferenceInput
          label="Produto"
          source="fuelId"
          reference="fuels"
          sort={{ field: "name", order: "ASC" }}
          emptyText="Todos"
          alwaysOn
        >
          <SelectInput
            emptyText="Todos"
            label="Produto"
            optionText={'name'}
          />
        </ReferenceInput>

        <SelectInput
          source="hasInvoice"
          label="Nota Fiscal"
          choices={[
            { name: "Sem nota", value: false },
            { name: "Com nota", value: true },
          ]}
          emptyText="Todos"
          optionText="name"
          optionValue="value"
          allowEmpty
          alwaysOn
        />

        <SelectInput
          source="isBilled"
          label="Status de Faturamento"
          choices={[
            { name: "Não faturado", value: false },
            { name: "Faturado", value: true },
          ]}
          emptyText="Todos"
          optionText="name"
          optionValue="value"
          allowEmpty
          alwaysOn
        />
      </Filter>
    );
  };

  const fillinsExporter = async (data, fetchRelatedRecords) => {
    let fillinInfo = data[0].data;

    const relations = [
      { field: 'fuelId', resource: 'fuels' },
      { field: 'stationId', resource: 'stations' },
      { field: 'chainId', resource: 'chains' },
      { field: 'subCompanyId', resource: 'sub-companies' },
      { field: 'invoiceId', resource: 'invoices' },
    ];

    const fetchData = await fetchRelatedRecords(relations, fillinInfo);

    const getData = (resource) => {
      return fetchData.find(({ resource: res }) => res === resource)?.results || [];
    }

    fillinInfo = fillinInfo.map(fillin => {
      const { chainId, fuelId, stationId, subCompanyId, invoiceId, ...fillinRest } = fillin;
      const chain = getData('chains').find(({ id }) => id === chainId);
      const station = getData('stations').find(({ id }) => id === stationId);
      const subCompany = getData('sub-companies').find(({ id }) => id === subCompanyId);
      const fuel = getData('fuels').find(({ id }) => id === fuelId);
      const invoice = getData('invoices').find(({ id }) => id === invoiceId);

      return {
        posto: station ? station.name : 'Não identificado',
        rede: chain ? chain.name : 'Não identificado',
        transportadora: subCompany ? subCompany.name : 'Não identificado',
        valor_a_pagar: Number(fillinRest.valueToPay).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
        taxa: fillinRest.taxRate ? `${fillinRest.taxRate}%` : '0%',
        vencimento_pagamento: fillinRest.dueDate ? dayjs(fillinRest.dueDate).format('DD/MM/YYYY') : '--',
        data_abastecimento: fillinRest.receivedAt ? dayjs(fillinRest.receivedAt).format('DD/MM/YYYY HH:mm') : '',
        combustivel: fuel ? fuel.name : 'Não identificado',
        nota_fiscal: invoice ? invoice.invoiceNumber : '--',
      };
    });

    return fillinInfo;
  }

  return (
    <ReportList
      {...props}
      basePath="fillins"
      title="Previsão de Pagamento"
      resource="reports/billing-forecast-payment"
      filters={<ListFilters />}
      sort={{ field: 'receivedAt', order: 'DESC' }}
      perPage={25}
      filterDefaultValues={{
        from: moment().startOf('day').toISOString(),
        to: moment().add(30, 'days').endOf('day').toISOString(),
      }}
      filter={filterValue}
      pagination={<ListPagination />}
      bulkActionButtons={false}
      disableSyncWithLocation
      fileName='relatorio_previsao_pagamento'
      customExporter={fillinsExporter}
      hasSummary={false}
      titleOnPrint="Relatório de Previsão de Pagamento"
      filterTablePrint={<FilterTablePrint />}
      landscape={true}
    >
      <FillinList />
    </ReportList>
  );
};

export default BillingForecastPaymentReport;
